import {
  getCurrentAuthUser,
  signInAnonymousUser,
  linkOrSignInGoogleWithUser,
  userHasGoogleProvider,
  signOutCurrentUser,
  signInWithGooglePopup,
  signInWithEmailPassword,
  createUserWithEmailPassword,
} from "../repositories/authRepository";
import {
  ensureUserProfile,
  syncUserProfileFromAuthUser,
} from "./userService";
import { reactivateClosedRetainAccount } from "./identityService";
import { normalizeRallyAllyAuthEmail } from "../domain/auth/rallyAllyAuthEmail";
import {
  trackAuthAfterGoogleCredential,
  trackLogin,
  trackLogout,
  trackSignupCompleted,
} from "./analytics/bridges/authAnalytics";

/**
 * Ensures a Firebase Auth session exists (anonymous).
 * @returns {Promise<import('firebase/auth').User>}
 */
export async function ensureAnonymousSession() {
  const existing = getCurrentAuthUser();
  if (existing) return existing;
  return signInAnonymousUser();
}

/**
 * Bootstrap Firestore user profile for an existing Firebase Auth user (no anonymous sign-in).
 * @param {import('firebase/auth').User} user
 */
export async function bootstrapUserSession(user) {
  if (!user) throw new Error("No signed-in user");
  let profile = await ensureUserProfile(user.uid);
  if (userHasGoogleProvider(user) || user.email) {
    profile = await syncUserProfileFromAuthUser(user);
  }
  try {
    await reactivateClosedRetainAccount(user.uid);
    profile = (await ensureUserProfile(user.uid)) || profile;
  } catch {
    // Reactivation is best-effort; sign-in must still succeed.
  }
  return { user, profile };
}

/** @deprecated Use bootstrapUserSession after explicit sign-in */
export async function bootstrapIdentity() {
  const user = await ensureAnonymousSession();
  return bootstrapUserSession(user);
}

/**
 * Google sign-in from the welcome screen (new session or existing Google account).
 */
export async function signInWithGoogleAccount() {
  const credential = await signInWithGooglePopup();
  const session = await bootstrapUserSession(credential.user);
  void trackAuthAfterGoogleCredential(credential, session.profile);
  return session;
}

/**
 * @param {string} userId RallyAlly user ID or email
 * @param {string} password
 */
export async function signInWithRallyAllyAccount(userId, password) {
  const email = normalizeRallyAllyAuthEmail(userId);
  const credential = await signInWithEmailPassword(email, password);
  const session = await bootstrapUserSession(credential.user);
  void trackLogin(credential.user, session.profile);
  return session;
}

/**
 * @param {string} userId
 * @param {string} password
 */
export async function registerRallyAllyAccount(userId, password) {
  const email = normalizeRallyAllyAuthEmail(userId);
  const credential = await createUserWithEmailPassword(email, password);
  const session = await bootstrapUserSession(credential.user);
  void trackSignupCompleted(credential.user);
  return session;
}

/**
 * Optional upgrade: link Google to the current anonymous session (same uid).
 */
export async function linkGoogleAccount() {
  const user = getCurrentAuthUser();
  if (!user) {
    return signInWithGoogleAccount();
  }
  if (userHasGoogleProvider(user)) {
    const profile = await syncUserProfileFromAuthUser(user);
    return { user, profile };
  }
  const linked = await linkOrSignInGoogleWithUser(user);
  const profile = await syncUserProfileFromAuthUser(linked);
  return { user: linked, profile };
}

/**
 * Signs out of Firebase. Auth listener clears session → welcome screen.
 */
export async function signOutSession() {
  trackLogout();
  await signOutCurrentUser();
}

/** Map Firebase Auth errors to short user-facing copy. */
export function friendlyAuthErrorMessage(error) {
  const code =
    error && typeof error === "object" && "code" in error ? String(error.code) : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
    return "Incorrect user ID or password.";
  }
  if (code === "auth/user-not-found") {
    return "No account found for that user ID. Create an account first.";
  }
  if (code === "auth/email-already-in-use") {
    return "That user ID is already registered. Try signing in.";
  }
  if (code === "auth/weak-password") {
    return "Password must be at least 6 characters.";
  }
  if (code === "auth/popup-closed-by-user") {
    return "Google sign-in was cancelled.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Wait a moment and try again.";
  }
  if (code === "auth/operation-not-allowed") {
    return "Email/password sign-in is not enabled for this app yet. Ask the operator to enable Email/Password sign-in.";
  }
  if (code === "auth/requires-recent-login") {
    return "For security, sign in again, then retry deleting your account.";
  }
  return message.replace(/^Firebase:\s*/i, "").trim() || "Something went wrong. Try again.";
}
